import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiUsers, 
  FiUserPlus, 
  FiSearch,
  FiRefreshCw
} from 'react-icons/fi';
import {
  GET_MY_FRIENDS,
  SEND_FRIEND_REQUEST,
  GET_MY_CHATROOMS
} from '../graphql/friends';
import FriendCard from '../components/FriendCard';
import UserDetailModal from '../components/UserDetailModal';

const Friends = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'add'
  const [searchUserId, setSearchUserId] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // GraphQL queries
  const { 
    data: friendsData, 
    loading: friendsLoading, 
    error: friendsError,
    refetch: refetchFriends 
  } = useQuery(GET_MY_FRIENDS, {
    skip: !user,
    errorPolicy: 'all'
  });

  const {
    data: chatroomsData,
    loading: chatroomsLoading,
    refetch: refetchChatrooms
  } = useQuery(GET_MY_CHATROOMS, {
    skip: !user,
    errorPolicy: 'all',
    onCompleted: (data) => {
      console.log('📬 [Friends] 聊天室數據已載入:', {
        chatrooms: data?.myChatrooms?.chatrooms,
        count: data?.myChatrooms?.chatrooms?.length
      });
      
      // 詳細顯示每個聊天室的未讀計數
      data?.myChatrooms?.chatrooms?.forEach(room => {
        console.log(`💬 聊天室 ${room.id}:`, {
          type: room.type,
          participants: room.participants.map(p => ({ id: p.id, username: p.username })),
          lastMessage: room.lastMessage,
          unreadCounts: room.unreadCounts
        });
      });
    }
  });


  // GraphQL mutations
  const [sendFriendRequest] = useMutation(SEND_FRIEND_REQUEST, {
    refetchQueries: ['GetMyFriends', 'GetPendingRequests'],
    awaitRefetchQueries: true
  });

  // URL 同步：讀取和管理 userId 參數
  useEffect(() => {
    const urlUserId = searchParams.get('userId');
    if (urlUserId && urlUserId !== selectedUserId) {
      setSelectedUserId(urlUserId);
    }
    if (!urlUserId && selectedUserId !== null) {
      setSelectedUserId(null);
    }
  }, [searchParams, selectedUserId]);

  // 刷新數據
  const handleRefresh = () => {
    refetchFriends();
    refetchChatrooms();
  };

  // 處理查看使用者詳情
  const handleViewDetails = (userId) => {
    setSelectedUserId(userId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('userId', userId);
      return next;
    });
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('userId');
      return next;
    });
  };

  // 發送好友請求
  const handleSendFriendRequest = async (e) => {
    e.preventDefault();
    if (!searchUserId.trim()) return;

    setSendingRequest(true);
    try {
      const { data } = await sendFriendRequest({
        variables: {
          input: {
            targetUserId: searchUserId.trim(),
            type: 'PENDING'
          }
        }
      });

      if (data.sendFriendRequest.success) {
        alert('好友請求已發送！');
        setSearchUserId('');
      } else {
        alert(data.sendFriendRequest.message || '發送失敗');
      }
    } catch (error) {
      console.error('發送好友請求失敗:', error);
      alert('發送失敗，請重試');
    } finally {
      setSendingRequest(false);
    }
  };

  // 獲取好友數據
  const friends = friendsData?.myFriends?.relationships || [];
  const chatrooms = chatroomsData?.myChatrooms?.chatrooms || [];

  // 根據好友的 userId 找到對應的聊天室
  const getChatroomForFriend = (friendUserId) => {
    return chatrooms.find(room => 
      room.type === 'DIRECT' && 
      room.participants.some(p => p.id === friendUserId)
    );
  };

  const tabs = [
    { 
      id: 'friends', 
      label: '我的好友', 
      icon: FiUsers, 
      count: friends.length 
    },
    { 
      id: 'add', 
      label: '添加好友', 
      icon: FiUserPlus 
    }
  ];

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-500">請先登入以查看好友</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* 頁面頭部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-yellow-600 bg-clip-text text-transparent">與好友聊天</h1>
            <p className="text-gray-600 mt-1">你的好友和聊天</p>
          </div>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <FiRefreshCw className="w-4 h-4 mr-2" />
            刷新
          </button>
        </div>
      </div>

      {/* 標籤頁導航 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* 內容區域 */}
      <div className="space-y-6">
        {/* 我的好友 */}
        {activeTab === 'friends' && (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                我的好友 ({friends.length})
              </h2>
              <p className="text-gray-600 text-sm">
                與好友聊天，也可以查看共同話題
              </p>
            </div>

            {friendsLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-2">載入中...</p>
              </div>
            ) : friendsError ? (
              <div className="text-center py-8">
                <p className="text-red-500">載入失敗：{friendsError.message}</p>
              </div>
            ) : friends.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FiUsers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  還沒有好友
                </h3>
                <p className="text-gray-600 mb-4">
                  開始添加好友，建立你的社交網絡吧！
                </p>
                <button
                  onClick={() => setActiveTab('add')}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  <FiUserPlus className="w-4 h-4 mr-2" />
                  添加好友
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {friends.map((relationship) => {
                  const otherUser = relationship.userA.id === user.id 
                    ? relationship.userB 
                    : relationship.userA;
                  const chatroom = getChatroomForFriend(otherUser.id);
                  
                  return (
                    <FriendCard
                      key={relationship.id}
                      relationship={relationship}
                      type="friend"
                      currentUserId={user.id}
                      onUpdate={handleRefresh}
                      chatroom={chatroom}
                      onViewDetails={handleViewDetails}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 添加好友 */}
        {activeTab === 'add' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                添加好友
              </h2>
              <p className="text-gray-600 text-sm">
                透過使用者 ID 添加新好友（可在個人資料頁面查看您的 ID）
              </p>
            </div>

            <div className="max-w-md">
              <form onSubmit={handleSendFriendRequest} className="space-y-4">
                <div>
                  <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                    使用者 ID
                  </label>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      id="userId"
                      value={searchUserId}
                      onChange={(e) => setSearchUserId(e.target.value)}
                      placeholder="輸入要添加的使用者 ID"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      disabled={sendingRequest}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={sendingRequest || !searchUserId.trim()}
                  className="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  {sendingRequest ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      發送中...
                    </>
                  ) : (
                    <>
                      <FiUserPlus className="w-4 h-4 mr-2" />
                      發送好友請求
                    </>
                  )}
                </button>
              </form>

              {/* 使用提示 */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  使用提示
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• 輸入準確的使用者名稱</li>
                  <li>• 等待對方接受你的好友請求</li>
                  <li>• 成為好友後可以開始聊天</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 使用者詳情彈窗 */}
      <UserDetailModal
        isOpen={selectedUserId !== null}
        onClose={handleCloseModal}
        userId={selectedUserId}
        sourceType="friends"
      />
    </div>
  );
};

export default Friends; 